import {
  BR_STATUS,
  getItemStatusCode,
  resolveBrUiStatus,
  isBrApprovedCode,
  resolveBrRejectReasonText,
  sanitizeBrRejectReasonForSupplier,
  groupBrListItems,
  pickBrDisplaySections,
  resolveBrHasHistory,
} from '../../../businessDocuments/utils/brDocument';
import {
  annotateBrItemsWithPendingNameChangeSource,
} from '../../../businessDocuments/utils/brNameChange';
import { loadAdminBrDocuments, parseAdminRejectDraftFromSavedText } from '../utils/brAdmin';

/**
 * 管理端 BR：公司上下文、列表加载、分区与更名来源（单一数据源）。
 */
export default {
  data() {
    return {
      loading: false,
      pendingItems: [],
      processedItems: [],
      documentList: [],
      nameChangeSourceList: [],
      cachedCurrentNameEn: '',
      cachedCurrentNameZh: '',
    };
  },
  computed: {
    empCompanyId() {
      const fromSim = this.simulatedSupplier?.empCompanyId
        ?? this.simulatedSupplier?.EmpCompanyId;
      const fromSimNum = Number(fromSim);
      if (Number.isFinite(fromSimNum) && fromSimNum > 0) {
        return fromSimNum;
      }
      const fromProp = Number(this.supplierId);
      return Number.isFinite(fromProp) && fromProp > 0 ? fromProp : 0;
    },
    supplierAccountId() {
      const fromSim = this.simulatedSupplier?.accountId
        ?? this.simulatedSupplier?.AccountId;
      const n = Number(fromSim);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
      const fromProp = Number(this.supplierId);
      return Number.isFinite(fromProp) && fromProp > 0 ? fromProp : 0;
    },
    currentNameEn() {
      const withName = this.allDisplayItems.find((x) => x.currentNameEn)
        || this.pendingItems[0]
        || this.processedItems[0];
      return withName?.currentNameEn || this.cachedCurrentNameEn || '—';
    },
    currentNameZh() {
      const withName = this.allDisplayItems.find((x) => x.currentNameZh)
        || this.pendingItems[0]
        || this.processedItems[0];
      return withName?.currentNameZh || this.cachedCurrentNameZh || '';
    },
    canRequestNameChange() {
      // nameChangeSourceList 已是 getApprovedBrForNameChangeOptions 结果
      return (this.nameChangeSourceList || []).length > 0;
    },
    allDisplayItems() {
      return [...this.pendingItems, ...this.processedItems];
    },
    pageListSections() {
      const pendingNameChanges = (this.pendingItems || []).filter(
        (item) => item?.isNameChangeRequestCard || item?.requestKind === 'NAME_CHANGE',
      );
      const annotated = annotateBrItemsWithPendingNameChangeSource(
        this.allDisplayItems,
        pendingNameChanges,
      );
      /* 上半：已生效+最新 Submit；下半：Last update 新→旧 */
      return groupBrListItems(annotated, this.$t.bind(this), {
        effectiveSectionLabelKey: 'lbl_EMPBE_BR_List_Section_Main',
      });
    },
    currentDisplayItems() {
      return this.displaySections.currentDisplayItems;
    },
    recordItems() {
      return this.displaySections.recordItems;
    },
    displaySections() {
      return pickBrDisplaySections(this.pageListSections);
    },
    /** 6c.2：已有任意 BR 历史但无已生效展示卡 */
    hasBrHistory() {
      return resolveBrHasHistory({
        documentList: this.documentList,
        recordItems: this.recordItems,
        extraItems: this.allDisplayItems,
      });
    },
  },
  watch: {
    empCompanyId: {
      immediate: true,
      handler() {
        this.loadPending();
      },
    },
  },
  methods: {
    itemUiStatus(item) {
      return resolveBrUiStatus(item);
    },
    isInEffectApprovedItem(item) {
      if (!isBrApprovedCode(item?.statusCode)) {
        return false;
      }
      const ui = this.itemUiStatus(item);
      return ui === BR_STATUS.READY || ui === BR_STATUS.APPROVED;
    },
    isPendingEffectiveApprovedItem(item) {
      if (!isBrApprovedCode(item?.statusCode)) {
        return false;
      }
      return this.itemUiStatus(item) === BR_STATUS.NOT_YET_EFFECTIVE;
    },
    countInEffectApprovedItems() {
      return (this.documentList || []).filter((row) => this.isInEffectApprovedItem(row)).length;
    },
    isLastInEffectApprovedItem(item) {
      return this.isInEffectApprovedItem(item) && this.countInEffectApprovedItems() <= 1;
    },
    canChangeToReject(item) {
      if (!isBrApprovedCode(item?.statusCode)) {
        return false;
      }
      if (this.isPendingEffectiveApprovedItem(item)) {
        return true;
      }
      if (!this.isInEffectApprovedItem(item)) {
        return false;
      }
      return this.countInEffectApprovedItems() > 1;
    },
    shouldShowDirectRejectBar(item) {
      return this.canChangeToReject(item) || this.isLastInEffectApprovedItem(item);
    },
    shouldShowAdminActions(item) {
      // 审批中(P)：等阿里云机器审，整栏操作区（Reject / Proceed）不展示
      if (getItemStatusCode(item) === BR_STATUS.PENDING) {
        return false;
      }
      if (item?.listKind === 'pending') {
        return true;
      }
      if (this.shouldShowDirectRejectBar(item)) {
        return true;
      }
      return getItemStatusCode(item) === BR_STATUS.REJECTED;
    },
    initRejectedDraft(item) {
      if (!item) return;
      const isRejected = getItemStatusCode(item) === BR_STATUS.REJECTED;
      if (!isRejected || !item.approvalId) return;
      // 回填：AI 短码/JSON 先转成可读文案，再按模板反勾选；匹配不上才进 OTHER
      const reasonText = sanitizeBrRejectReasonForSupplier(
        resolveBrRejectReasonText(item, this.$t.bind(this)),
      );
      const parsed = parseAdminRejectDraftFromSavedText(
        reasonText,
        this.rejectReasonOptions || [],
      );
      const draft = this.ensureDraft(item.approvalId);
      draft.rejectSelected = true;
      draft.reasonError = false;
      draft.otherReasonError = false;
      draft.reasons = parsed.reasons;
      draft.otherReasonText = parsed.otherReasonText;
    },
    async loadPending() {
      const empCompanyId = this.empCompanyId;
      if (!empCompanyId) {
        this.pendingItems = [];
        this.processedItems = [];
        this.documentList = [];
        this.nameChangeSourceList = [];
        return;
      }
      this.loading = true;
      try {
        const result = await loadAdminBrDocuments(empCompanyId);
        this.documentList = result.documentList || [];
        this.nameChangeSourceList = result.nameChangeSourceList || [];
        this.pendingItems = result.pendingItems || [];
        this.processedItems = result.processedItems || [];
        if (result.currentNameEn) {
          this.cachedCurrentNameEn = result.currentNameEn;
          this.cachedCurrentNameZh = result.currentNameZh || this.cachedCurrentNameZh;
        }
        this.drafts = {};
        this.resetProceedLockState?.();
        [...(this.pendingItems || []), ...(this.processedItems || [])].forEach((item) => {
          this.initRejectedDraft(item);
        });
        if (result.error) {
          this.$toast?.error?.(
            result.error === 'LOAD_FAILED'
              ? this.$t('lbl_EMPBE_BR_Admin_LoadFailed')
              : result.error,
          );
        } else if (result.listLoadWarning === 'LIST_LOAD_FAILED') {
          this.$toast?.error?.(this.$t('lbl_EMPBE_BR_Admin_HistoryLoadFailed'));
        }
      } catch (e) {
        this.pendingItems = [];
        this.processedItems = [];
        this.documentList = [];
        this.nameChangeSourceList = [];
        this.$toast?.error?.(e?.message || this.$t('lbl_EMPBE_BR_Admin_LoadFailed'));
      } finally {
        this.loading = false;
      }
    },
    getNameChangeDocumentList() {
      return this.documentList || [];
    },
    onNameChangeCannotOpen() {
      this.$toast?.error?.(this.$t('lbl_EMPBE_BR_NameChange_Validation_SelectBr'));
    },
  },
};
